import * as React from 'react';

/**
 * Whether an element is in (or near) the viewport, kept up to date.
 *
 * Not `useScrolled`: that measures distance from the top of the page, and the
 * thing this watches — the end of a list — moves every time the list grows.
 * Not `useReveal` either: that fires once, and a marker that stays in view
 * after a short page has loaded must keep reporting so the next one follows.
 *
 * Returns a callback ref rather than taking a ref object, so it works on an
 * element that mounts and unmounts — a marker that only exists while there is
 * more to load. While `enabled` is false nothing is observed and the answer is
 * `false`.
 */
export function useInView<T extends Element>({
  rootMargin = '0px',
  enabled = true,
}: {
  /** Grows the viewport for the test — `'0px 0px 600px 0px'` counts 600px below the fold. */
  rootMargin?: string;
  enabled?: boolean;
} = {}) {
  const [element, setElement] = React.useState<T | null>(null);
  const [inView, setInView] = React.useState(false);

  React.useEffect(() => {
    if (!element || !enabled || typeof IntersectionObserver === 'undefined') {
      setInView(false);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => setInView(entries.some((entry) => entry.isIntersecting)),
      { rootMargin },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, enabled, rootMargin]);

  return [setElement, inView] as const;
}
